// BackupDialog — choose what a per-database backup includes, then stream it to
// disk as a .sql / .sql.gz download. Opened from the Objects-tab toolbar and the
// sidebar database right-click menu (see Browser.jsx). The actual dump is built
// server-side and streamed (bounded memory) via lib/export.js::downloadBackup.
//
// Props:
//   connectionId  number
//   database      string
//   easy          bool   — simplified UI: hide the include/option checkboxes
//                          (everything defaults on), show only the note + Download
//   serverMode    bool   — save the dump on the SERVER (Backup/Restore tab) via
//                          createServerBackup instead of streaming a download
//   onClose       fn()
import { createSignal, createResource, Show } from 'solid-js';
import Modal from './Modal.jsx';
import ProgressModal from './ProgressModal.jsx';
import { downloadBackup } from '../lib/export.js';
import { createServerBackup } from '../lib/serverbackups.js';
import { loadBackupModes } from '../lib/backupprefs.js';
import { backupSampleRows } from '../lib/prefs.js';
import { toast } from '../lib/toast.js';

export default function BackupDialog(props) {
  // Defaults match a "complete, restorable backup": everything on, consistent
  // snapshot on, gzip on (backups are stored), DEFINER kept (faithful). The
  // option set is small and always sent together, so one object signal beats
  // a dozen scattered booleans.
  const [opts, setOpts] = createSignal({
    data: true, triggers: true, routines: true, views: true, events: true,
    singleTransaction: true, gzip: true, stripDefiner: false,
    honorStructureOnly: true, // honour the shared per-table structure-only tags
  });
  const set = (key, val) => setOpts((o) => ({ ...o, [key]: val }));

  // How many tables in this DB carry a backup tag (for the option hint).
  const [tags] = createResource(
    () => [props.connectionId, props.database],
    async ([id, db]) => {
      try {
        const { structureOnly, sampleData, skip } = await loadBackupModes(id, db);
        return { structure: structureOnly.length, sample: sampleData.length, skip: skip.length };
      } catch { return { structure: 0, sample: 0, skip: 0 }; }
    },
  );
  const tagCount = () => (tags()?.structure ?? 0) + (tags()?.sample ?? 0) + (tags()?.skip ?? 0);
  const [busy, setBusy] = createSignal(false);
  const [note, setNote] = createSignal(''); // optional label folded into the filename
  // Strict mode (server backups only): embed a correctness manifest so a strict
  // restore can verify structure + per-table row counts.
  const [correctness, setCorrectness] = createSignal(false);
  // Data checksum: add a per-table CRC32 to the manifest so a strict restore can
  // verify row CONTENTS, not just counts. Rides on correctness (needs the manifest).
  const [checksum, setChecksum] = createSignal(false);
  const [token, setToken] = createSignal(null); // set once the dump starts → show progress

  const start = async () => {
    setBusy(true);
    try {
      const o = opts();
      const spec = {
        connectionId: props.connectionId, database: props.database,
        note: note().trim(), ...o,
        // Easy mode is always a FULL backup — never honor per-table tags. The
        // include/option checkboxes (incl. "Honor per-table backup tags") are
        // hidden in easy mode, so this is the only place to force it off.
        honorStructureOnly: props.easy ? false : o.honorStructureOnly,
      };
      spec.correctness = !props.easy && correctness();
      spec.checksum = spec.correctness && checksum();
      if (props.serverMode) {
        // Server mode: fire-and-close — the dump runs on the server and shows
        // up in the tab's "Active operations" table (no modal).
        await createServerBackup(spec);
        props.onStarted?.();
        props.onClose?.();
        return;
      }
      // Download: stream a browser download, show the shared progress modal.
      const tok = await downloadBackup(spec);
      setToken(tok);
    } catch (e) {
      toast.error('Backup failed: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const Check = (cprops) => (
    <label class="backup-opt">
      <input type="checkbox" checked={opts()[cprops.k]} onChange={(e) => set(cprops.k, e.target.checked)} />
      <span>
        <span class="backup-opt-label">{cprops.label}</span>
        <Show when={cprops.hint}><span class="backup-opt-hint">{cprops.hint}</span></Show>
      </span>
    </label>
  );

  return (
    <Show when={!token()} fallback={
      <ProgressModal
        token={token()}
        title={`Backing up — ${props.database}`}
        kind="backup"
        onClose={props.onClose}
      />
    }>
      <Modal open={true} title={`Backup database — ${props.database}`} onClose={props.onClose}>
      <div class="backup-dialog">
        <p class="muted" style="margin-top:0;">
          Produces a SQL dump (CREATE + INSERT) using this server's own
          <span class="mono"> SHOW CREATE</span> output, so the DDL matches this
          server's version. {props.serverMode ? 'Saved on the server — it appears in this list when done.' : 'Streamed straight to a download.'}
        </p>

        {/* Optional note → folded into the download filename, e.g.
            "{conn}-{db}-{timestamp}-pre_upgrade.sql.gz". Always shown (it's the
            one input easy mode keeps). */}
        <label class="backup-opt" style="align-items:stretch;flex-direction:column;gap:4px;">
          <span class="backup-opt-label">Backup note <span class="muted" style="font-weight:400;">(optional — added to the filename)</span></span>
          <input
            class="input"
            value={note()}
            onInput={(e) => setNote(e.currentTarget.value)}
            placeholder="e.g. pre-upgrade, monthly, before-migration"
            disabled={busy()}
          />
        </label>

        {/* Include / Options checkboxes — hidden in easy mode (everything stays
            on via the opts() defaults). */}
        <Show when={!props.easy}>
        <div class="backup-section-title">Include</div>
        <Check k="data" label="Table data (INSERTs)" />
        <Show when={tagCount() > 0}>
          <label class="backup-opt" style="margin-left:22px;">
            <input
              type="checkbox"
              checked={opts().honorStructureOnly}
              disabled={!opts().data}
              onChange={(e) => set('honorStructureOnly', e.target.checked)}
            />
            <span>
              <span class="backup-opt-label">Honor per-table backup tags</span>
              <span class="backup-opt-hint">
                {[
                  tags()?.structure ? `${tags().structure} structure-only` : '',
                  tags()?.sample ? `${tags().sample} sample (${backupSampleRows()} rows)` : '',
                  tags()?.skip ? `${tags().skip} excluded` : '',
                ].filter(Boolean).join(' · ')} — uncheck for a full backup (all data, every table)
              </span>
            </span>
          </label>
        </Show>
        <Check k="triggers" label="Triggers" />
        <Check k="routines" label="Stored routines (procedures + functions)" />
        <Check k="views" label="Views" />
        <Check k="events" label="Events" />

        <div class="backup-section-title">Options</div>
        <Check k="singleTransaction" label="Consistent snapshot"
               hint="Single transaction — point-in-time consistent for InnoDB" />
        <Check k="gzip" label="Compress (.sql.gz)" />
        <Check k="stripDefiner" label="Strip DEFINER clauses"
               hint="Restore views/routines under the importing account" />
        {/* Strict correctness — embeds a manifest the strict restore verifies
            against (works for download + server backups). */}
        <label class="backup-opt">
          <input type="checkbox" checked={correctness()} onChange={(e) => setCorrectness(e.target.checked)} />
          <span>
            <span class="backup-opt-label">Strict (embed correctness manifest)</span>
            <span class="backup-opt-hint">Records the structure + per-table row counts so a strict restore can verify the result. Slightly larger backup.</span>
          </span>
        </label>
        {/* Data checksum — nested under strict; only meaningful with a manifest. */}
        <Show when={correctness()}>
          <label class="backup-opt" style="margin-left:22px;">
            <input type="checkbox" checked={checksum()} onChange={(e) => setChecksum(e.target.checked)} />
            <span>
              <span class="backup-opt-label">Verify data contents (CRC32)</span>
              <span class="backup-opt-hint">Adds an order-independent per-table data checksum so a strict restore catches row-content drift (e.g. silent charset truncation), not just row counts. Scans every table once — slower on big databases.</span>
            </span>
          </label>
        </Show>
        </Show>

        <div class="backup-actions">
          <button class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Cancel</button>
          <button class="btn btn-primary" onClick={start} disabled={busy()}>
            {busy() ? '⏳ Preparing…' : (props.serverMode ? '💾 Back up to server' : '⬇ Download backup')}
          </button>
        </div>
      </div>
      </Modal>
    </Show>
  );
}
